import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { EProcessStatus, ESigningOrderType, IActivityDto } from 'core/dto';
import { filterAllTruthyInTuple, mapKey } from 'core/rx-js';
import { statusDisplays } from 'projects/designer/src/app/utils/status-display';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { ITask } from '../../../../dto-map/i-task';
import { ProcessReviewService } from '../process-review.service';

@Component({
  selector: 'ss-process-review-activity',
  templateUrl: './process-review-activity.component.html',
  styleUrls: ['./process-review-activity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessReviewActivityComponent {


  readonly activitySubject = new BehaviorSubject<IActivityDto>(null)
  @Input() get activity() {
    return this.activitySubject.value
  }
  set activity(value) {
    this.activitySubject.next(value)
  }

  readonly actorObs = this.activitySubject.pipe(
    filter(Boolean),
    map(({ actor }) => actor),
    filter(Boolean)
  )

  readonly actorDisplayObs = this.actorObs.pipe(
    filter(Boolean),
    map(actor => actor.firstName + ' ' + actor.lastName)
  )

  readonly showIdxObs = this._processReviewService.processSubject.pipe(
    map((process) => process.signingOrderType !== ESigningOrderType.paralel)
  )


  readonly tasksSubject = this._processReviewService.tasksSubject;
  readonly pdfDocumentsSubject = this._processReviewService.pdfDocumentsSubject;

  readonly activityTasksObs = combineLatest([this.tasksSubject, this.activitySubject])
    .pipe(
      filterAllTruthyInTuple(),
      map(([tasks, activity]) => tasks.filter(({ activityId, type }) => activity.id === activityId && type === 'SIGNATURE') as ITask<'SIGNATURE'>[]),

    )

  readonly activityDocumentsDisplayObs = combineLatest([this.tasksSubject, this.pdfDocumentsSubject]).pipe(
    filterAllTruthyInTuple(),
    map(([tasks, documents]) => {

      const activityDocuments = new Set<string>(tasks.map((task) => task.documentId));

      return documents.filter(document => activityDocuments.has(document.id))
    }),
    map((documents) => documents.map(({ name }) => name).join(', '))
  )

  readonly subtitleDisplayObs = this.activitySubject.pipe(
    filter(Boolean),
    map((activity) => [
      { label: 'ss-entities.process.status', value: activity.status, display: statusDisplays[activity.status] },
    ]
      .filter(({ value }) => Boolean(value))
    )
  )

  readonly showStatusChangedDateObs = this.activitySubject.pipe(
    map((activity) => [EProcessStatus.Rejected, EProcessStatus.Cancelled, EProcessStatus.Finished].includes(activity?.status)),
    distinctUntilChanged()

  )

  readonly showRejectionReasonObs = this.activitySubject.pipe(
    map((activity) => activity?.status === EProcessStatus.Rejected),
    distinctUntilChanged()
  )


  readonly emailObs = this.actorObs.pipe(
    map((actor) => actor.email)
  )

  readonly statusDisplayObs = this.activitySubject.pipe(
    filter(Boolean),
    map(({ status }) => statusDisplays[status])
  )

  readonly statusChangedReasonObs = this.activitySubject.pipe(
    filter(Boolean),
    mapKey('statusChangedReason')
  )

  readonly statusDisplayClassObs = this.activitySubject.pipe(
    filter(Boolean),
    map((activity) => {
      switch (activity.status) {
        case EProcessStatus.Cancelled:
        case EProcessStatus.Rejected:
        case EProcessStatus.Expired:
          return 'warn-text'
        case EProcessStatus.Finished:
          return 'accent-text'

      }

      return 'primary-text'
    }),
    distinctUntilChanged()
  )

  readonly statusChangedDateObs = this.activitySubject.pipe(
    filter(Boolean),
    mapKey('statusChanged'),
    map(date => formatDate(date, 'dd/MM/yyyy, HH:mm', 'en-us'))
  )

  constructor(
    private readonly _processReviewService: ProcessReviewService,

  ) {

  }
} 
